import { getFeaturedBlockchain } from "@/app/blockchains/featured-blockchain/get-featured-blockchain"
import type { UnionAddress } from "@anaconda/core/blockchain/address"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { usePathname } from "next/navigation"
import { ProfilePageType } from "./profile-page-type"
import { buildProfileHrefPath } from "./profile-path"

function extractBlockchainFromPathname(pathname: string): Blockchain | null {
  const paths = pathname.split("/").filter(Boolean)
  if (paths.length > 0) {
    try {
      return paths[0].toUpperCase() as Blockchain
    } catch {
      return null
    }
  }
  return null
}

export const getProfileHref = (
  walletAddress: UnionAddress,
  selectedBlockchain?: Blockchain,
) => buildProfileHrefPath(walletAddress, selectedBlockchain)

const HREF_FACTORY_BY_PAGE_TYPE: Record<
  ProfilePageType,
  (walletAddress: UnionAddress, selectedBlockchain?: Blockchain) => string
> = {
  [ProfilePageType.ITEMS]: (walletAddress, selectedBlockchain) =>
    getProfileHref(walletAddress, selectedBlockchain),
  [ProfilePageType.LISTINGS]: (walletAddress, selectedBlockchain) =>
    `${getProfileHref(walletAddress, selectedBlockchain)}/listings`,
  [ProfilePageType.ACTIVITY]: (walletAddress, selectedBlockchain) =>
    `${getProfileHref(walletAddress, selectedBlockchain)}/activity`,
  [ProfilePageType.OFFERS]: (walletAddress, selectedBlockchain) =>
    `${getProfileHref(walletAddress, selectedBlockchain)}/offers`,
}

export const useProfilePageHref = (
  walletAddress: UnionAddress,
  pageType = ProfilePageType.ITEMS,
  selectedBlockchain?: Blockchain,
) => {
  const pathname = usePathname()

  // If no selectedBlockchain provided, try to extract from current URL
  const blockchainToUse =
    selectedBlockchain ||
    extractBlockchainFromPathname(pathname) ||
    getFeaturedBlockchain()

  return HREF_FACTORY_BY_PAGE_TYPE[pageType](walletAddress, blockchainToUse)
}

export const useProfileActiveLinkChecker = (
  walletAddress: UnionAddress,
  pageType: ProfilePageType,
  selectedBlockchain?: Blockchain,
) => {
  const urlPath = usePathname()
  const pathToCheck = useProfilePageHref(
    walletAddress,
    pageType,
    selectedBlockchain,
  )

  return urlPath === pathToCheck
}
